import React from 'react';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { SwitchProps } from '@mui/material/Switch';

interface SchedulesStatusSwitchProps {
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * ## SchedulesStatusSwitch component
 * Switch component for toggling the Schedule Status information section.
 *
 * @param {boolean} checked - Whether the switch is checked
 * @param {(event: React.ChangeEvent<HTMLInputElement>) => void} onChange - Handler for switch changes
 * @returns {JSX.Element} The rendered SchedulesStatusSwitch component
 */
const SchedulesStatusSwitch = React.memo(function SchedulesStatusSwitch({
  checked,
  onChange,
}: SchedulesStatusSwitchProps) {
  return (
    <FormControlLabel
      control={
        <IosSwitch
          checked={checked}
          onChange={onChange}
          inputProps={{ 'aria-label': 'Status da escala' }}
        />
      }
      label={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 500,
              fontSize: '13px',
              lineHeight: '1.3',
              color: '#00A5FF',
            }}
          >
            Status da Escala
          </Typography>
          <Box
            sx={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#FF6B00',
            }}
          />
        </Box>
      }
      labelPlacement='start'
      sx={{
        margin: 0,
        '& .MuiFormControlLabel-label': {
          marginLeft: 0,
          marginRight: '10px',
        },
      }}
    />
  );
});

const IosSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName='.Mui-focusVisible' disableRipple {...props} />
))(({ theme }) => ({
  width: 38,
  height: 22,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 1,
    transitionDuration: '200ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#ffffff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#00A5FF',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      boxShadow: '0 0 0 4px rgba(0, 165, 255, 0.25)',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[300],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.4,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 20,
    height: 20,
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 6px rgba(9, 32, 86, 0.18)',
  },
  '& .MuiSwitch-track': {
    borderRadius: 11,
    backgroundColor: '#E6ECF5',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 200,
    }),
  },
}));

export default SchedulesStatusSwitch;
